import React, { MouseEventHandler, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { Button as BaseButton } from '@osmosis/react-ui/components/Button';
import { Flex } from '@osmosis/react-ui/components/Box';
import { P } from '@osmosis/react-ui/components/Text';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { useSchemeContext } from '@osmosis/react-ui/context/SchemeContext';

import { QuizConfidence } from '../../types/quiz';
import { ReverseQuizButtons, TextDivider } from './types/utils';
import {
  shortcutsIds,
  SHORTCUTS_QUIZ_DEFAULT,
} from '../../constants/quizShortcuts';
import { useQuizContext } from '../../context/QuizContext';
import { useQuizShortcutsContext } from '../../context/QuizShortcutsContext';
import { useQuizItems } from '../../context/QuizItemsProvider';
import { QuizSettings } from '../../lib/shared/quiz/quizSetting';

/**
 * Each Quiz Type Component mixes this where appropriate.
 * The `isCorrect` function gets called when a submit button is clicked.
 * And the parent component will check based on its state if the answer was correct or not.
 */
type Props = {
  submit: (confidence: QuizConfidence) => void;
};

type Confidence = 'low' | 'medium' | 'high';

const Button = ({
  children,
  confidence,
  submit,
  id,
}: {
  children: React.ReactNode;
  confidence: QuizConfidence;
  submit: Props['submit'];
  id: keyof typeof SHORTCUTS_QUIZ_DEFAULT;
}) => {
  const colors = useColors();
  const { scheme } = useSchemeContext();
  const { shortcuts } = useQuizShortcutsContext();

  const handleSubmit = () => {
    submit(confidence);
  };

  const makeTitle = (): string => {
    const btnTitle = (): string => {
      switch (confidence) {
        case 1:
          return 'You have no idea.';
        case 2:
          return 'You might know it.';
        case 3:
          return 'You definitely know it.';
        default:
          return '';
      }
    };

    return `${btnTitle()} Shortcut key: ${shortcuts[id]}`;
  };

  return (
    <Flex>
      <BaseButton
        data-testid={`btn-conf-${confidence}`}
        data-tip
        data-tooltip-id={`btn-conf-${confidence}`}
        onClick={
          handleSubmit as unknown as MouseEventHandler<HTMLButtonElement>
        }
        flex={1}
      >
        {children}
      </BaseButton>
      <Tooltip
        id={`btn-conf-${confidence}`}
        style={{
          backgroundColor: colors.gray80,
          color: scheme === 'dark' ? colors.dark : colors.white,
        }}
        place="top"
      >
        <P color="inherit" fontSize={13}>
          {makeTitle()}
        </P>
      </Tooltip>
    </Flex>
  );
};

/**
 * QuizSubmitButtons.
 */
const QuizSubmitButtons = ({ submit }: Props) => {
  const intl = useIntl();

  const buttons: Confidence[] = ['low', 'medium', 'high'];
  const confidenceButtonMap = {
    low: intl.formatMessage({
      id: 'quiz.submit.low',
      defaultMessage: 'Low',
    }),
    medium: intl.formatMessage({
      id: 'common.medium',
      defaultMessage: 'Medium',
    }),
    high: intl.formatMessage({
      id: 'common.high',
      defaultMessage: 'High',
    }),
  };

  const getConfidence = (title: Confidence) =>
    title.toUpperCase() as keyof typeof QuizConfidence;

  const { quizSettings } = useQuizContext();
  const quizButtonsReversed = quizSettings[QuizSettings.BUTTONS_REVERSED];
  const { setOnConfidenceKeyHandler } = useQuizItems();

  useEffect(() => {
    // Register the submit handler for the quiz buttons
    setOnConfidenceKeyHandler(() => (confidence: QuizConfidence) => {
      submit(confidence);
    });

    return () => {
      // Unregister on unmount
      setOnConfidenceKeyHandler(undefined);
    };
  }, []);

  return (
    <Flex
      overflow="hidden"
      flexDirection="column"
      sx={{
        boxSizing: 'border-box',
        alignItems: 'center',
        mt: 4,
      }}
    >
      <TextDivider />
      <Flex
        mt={2}
        sx={{
          gap: 3,
        }}
      >
        {(quizButtonsReversed ? buttons.reverse() : buttons).map((btn, i) => (
          <Button
            data-testid={`quiz-confidence-submit-${btn}-btn`}
            confidence={QuizConfidence[getConfidence(btn)]}
            submit={submit}
            key={i}
            id={shortcutsIds[btn]}
          >
            {confidenceButtonMap[btn]}
          </Button>
        ))}
      </Flex>
      <ReverseQuizButtons />
    </Flex>
  );
};

export default QuizSubmitButtons;
